/* eslint-disable react/no-danger */
import HomeHero from '@/components/HomeHero/HomeHero';
import LearnMoreBlogs from "@/components/LearnMoreBlogs";
import validatePaidCampaign from '@helpers/validatePaidCampaign';
import axios from 'axios';
import dynamic from 'next/dynamic';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import FooterComponent from '@/components/Footer/FooterComponent';
import Header from '@/components/Header/Header';
import SEOHead from '@/components/SEOHead/SEOHead';
import { WordPressAPI } from '../../config';
import sanitizeHtml from 'sanitize-html';

const PullUpScheduler = dynamic(() =>
  import('@/components/PullUpScheduler/PullUpSchedulerV2')
);

const TrustPilotComponent = dynamic(
  () => import('@/components/TrustPilot/TrustPilotComponent'),
  { ssr: false }
);

const Faq = dynamic(
  () => import("@/components/EntityFormation/shared/Faq/Faq"),
  { ssr: false }
);

const EndCTAComponent = dynamic(
  () => import('@components/Bookkeeping/EndCTAComponent'),
  { ssr: false }
);
const FeaturesComponent = dynamic(
  () => import('@components/Bookkeeping/FeaturesComponent'),
  { ssr: false }
);
const StatsComponent = dynamic(
  () => import('@components/Bookkeeping/StatsComponent'),
  { ssr: false }
);
const TestimonialsComponent = dynamic(
  () => import('@components/Bookkeeping/TestimonialsComponent'),
  { ssr: false }
);
const CertComponent = dynamic(
  () => import('@components/Bookkeeping/CertComponent'),
  { ssr: false }
);
const BoxedFeaturesComponent = dynamic(
  () => import('@components/Bookkeeping/BoxedFeaturesComponent'),
  { ssr: false }
);
const CTAListComponent = dynamic(
  () => import('@components/Bookkeeping/CTAListComponent'),
  { ssr: false }
);

const FooterNewsModal = dynamic(import('@/components/FooterNewsModal'));


const TPStyles = styled.div`
background-color: #fff;
padding: 20px 0px;
position: relative;

.tp-container {
  margin: 0 auto;
  max-width: 100%;
  
  ${props => props.theme.media.md} {
    max-width: 67%;
  }
}
`;

const BookkeepingStyles = styled.div`
.hero-wrap {
  ${props => props.theme.media.md} {
    .customers {
      margin: auto;
      margin-left: 0;
    }
  }
  
  .title {
    width: 100%;
    font-size: 40px;
    ${props => props.theme.media.md} {
      font-size: 50px;
      max-width: 575px;
    }
  }
  
  .sub-header p {
    width: 100%;
    font-size: 16px;
    ${props => props.theme.media.md} {
      display: block;
      margin: auto;
      font-size: 20px;
      max-width: 555px;
    }
    
    strong {
      ${props => props.theme.media.md} {
        display: block;
        margin-top: 20px;
        margin-left: -20px;
        margin-right: -20px;
        font-size: 22px;
        font-weight: 500;
        margin-bottom: 10px;
      }
    }
  }
  
  .customers {
    display: none;
    ${props => props.theme.media.md} {
      display: block;
    }
  }
  
  .btn-wrap {
    position: static;
    transform: none;
    margin-bottom: 30px;
  }
  
}

.final-cta-container {
  h2 {
    @media (max-width: 405px) {
      font-size: 24px;
     }
    @media (min-width: 405px) {
      white-space: pre;
     }
  }
}
`;

const sanitizeSettings = {
  allowedTags: ['p', 'strong'],
};

const scriptData = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [{
    "@type": "Question",
    "name": "Do small businesses need a bookkeeper?",
    "acceptedAnswer": {
      "@type": "Answer",
      "text": "Small businesses do not require a bookkeeper, but we recommend using one to avoid penalties and increased scrutiny from the IRS. When you work with 1-800Accountant, your books will be organized with accuracy guaranteed, giving you the freedom to focus on growing your business."
    }
  }, {
    "@type": "Question",
    "name": "What is catch-up bookkeeping?",
    "acceptedAnswer": {
      "@type": "Answer",
      "text": "Small businesses use catch-up bookkeeping when they're behind on their books. 1-800Accountant offers a robust catch-up bookkeeping service to small businesses. No matter how far behind you are – even if you’re years behind – we can get you caught up quickly."
    }
  }]
});

const endCtaTitleObj = {
  title: "Talk to 1-800Accountant. Say hello to better bookkeeping.",
  index: 24
}

const Bookkeeping = ({ data, blogModuleData }) => {
  const { title, acf } = data;

  const paid_campaign = validatePaidCampaign(acf.paid_campaign);
  const [isSchedulerOpen, setIsSchedulerOpen] = useState(false);
  const [hideSchedulerHeader, setHideSchedulerHeader] = useState(false);

  useEffect(() => {
    if (isSchedulerOpen) {
      setHideSchedulerHeader(true);
    } else {
      setHideSchedulerHeader(false);
    }
  }, [isSchedulerOpen])

  const heroData = {
    hero_background_image: acf.hero_background_image,
    hero_title: acf.hero_title,
    hero_sub_title: acf.hero_sub_title,
    hero_image: acf.hero_image
  }

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      return isSchedulerOpen
        ? document.body.classList.add('no-overflow')
        : document.body.classList.remove('no-overflow');
    }

    return () => {
      isMounted = false;
    };
  }, [isSchedulerOpen]);

  const openFormModal = () => {
    setIsSchedulerOpen(true);
  };

  return (
    <BookkeepingStyles>
      <SEOHead>
        <title>{acf?.page_title_tag || title.rendered}</title>
        <meta
          name="description"
          content={acf?.meta_description || ''}
          key="description"
        />
        <script
          type="text/javascript"
          src="//app-ab33.marketo.com/js/forms2/js/forms2.min.js"
        />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: sanitizeHtml(scriptData) }} />
      </SEOHead>
      <Header
        PaidCampaign={typeof (paid_campaign) === 'undefined' ? 1 : paid_campaign}
        phone_number={acf.phone_number_setup}
        leadValues={acf.lead_values}
        AdditionalQuestions={acf.additional_questions}
        hideSchedulerHeader={hideSchedulerHeader}
      />
      <HomeHero
        data={
          heroData
        }
        handleScheduler={openFormModal}
      />
      <TPStyles>
        <div className="tp-container">
          <TrustPilotComponent
            templateID="5406e65db0d04a09e042d5fc"
            height="28px"
            alt
          />
        </div>
      </TPStyles>

      <CertComponent
        title={acf.certificate.title}
        image={acf.certificate.image}
        description={acf.certificate.description}
      />

      <FeaturesComponent title={acf.icons_section.title} features={acf.icons_section.icons} />

      <CTAListComponent
        title={acf.cta_list.title}
        list_text={acf.cta_list.list_text}
        image={acf.cta_list.image}
        handleScheduler={openFormModal} />

      <StatsComponent
        stats={acf.stats_section}
      />

      <TestimonialsComponent
        testimonials={acf.testimonials}
      />

      <BoxedFeaturesComponent
        main_title={acf.feature_list.title}
        main_features={acf.feature_list.features}
        help_title={acf.how_we_help.title}
        help_features={acf.how_we_help.list}
      />

      <div className="faq-section">
        <Faq title={acf.common_content.faqs.title} faqs={acf.common_content.faqs.questions} />
      </div>

      <LearnMoreBlogs
        blogModuleData={blogModuleData}
        section_subtitle={acf.section_subtitle}
        wwo_section_title={acf.wwo_section_title}
      ></LearnMoreBlogs>

      <EndCTAComponent
        title="Talk to 1-800Accountant. Say hello to better bookkeeping."
        handleScheduler={openFormModal}
        splitTitle={endCtaTitleObj}
        customClass="bookkeeping"
      />
      {/* Email Newsletter */}
      <FooterNewsModal />
      <FooterComponent
        phone_number={acf.phone_number_setup}
      />

      {isSchedulerOpen && (
        <PullUpScheduler
          setIsSchedulerOpen={setIsSchedulerOpen}
          AdditionalQuestions={acf.additional_questions}
          leadValues={acf.lead_values}
        />
      )}
    </BookkeepingStyles>
  );

}

export default Bookkeeping;

export const getStaticProps = async () => {
  const GET_POST_URL = `${process.env.NEXT_PUBLIC_BLOG}/wp-json/wp/v2/posts`;

  const axiosWP = axios.get(`${WordPressAPI.pages}/286`);
  const axiosBlog1 = axios.get(`${GET_POST_URL}?slug=common-bookkeeping-terms&_embed=true`);
  const axiosBlog2 = axios.get(`${GET_POST_URL}?slug=create-simple-bookkeeping-spreadsheet-business&_embed=true`);
  const axiosBlog3 = axios.get(`${GET_POST_URL}?slug=how-to-find-a-virtual-bookkeeper&_embed=true`);

  const [{ data }, blogData1, blogData2, blogData3] = await Promise.all([axiosWP, axiosBlog1, axiosBlog2, axiosBlog3]);

  const post1 = blogData1.data[0];
  const post2 = blogData2.data[0];
  const post3 = blogData3.data[0];

  const blogModuleData = [post1, post2, post3];

  return {
    props: {
      data,
      blogModuleData
    },
    // revalidate: 1,
  };
};
